/**
 * Service API pour Comité TI
 * Gestion des réunions, participants, agenda, notes, actions
 */

import { DEFAULT_CLIENT_ID } from '../config/client';

const API_BASE_URL = '/api/prisme/comite';

export type ComiteScope = 'CLIENT_TI' | 'PRISME' | 'CE' | (string & {});

export interface ComiteReunion {
  id?: number;
  client_id: number;
  clientId?: number;
  comite_scope?: string;
  comiteScope?: ComiteScope;
  nom?: string;
  date_reunion: string;
  heure_debut?: string;
  lieu?: string;
  type_rencontre?: string;
  statut?: string;
  duree_prevue_min?: number;
  objectif_principal?: string;
  verbatim?: string;
  date_creation?: string;
  date_modification?: string;
  counts?: {
    participants: number;
    agenda: number;
    notes: number;
    actions: number;
    hasResume: boolean;
    checklist?: {
      total: number;
      validated: number;
      mandatory: number;
      validatedMandatory: number;
      isValidated: boolean;
    };
  };
}

export interface ComiteParticipant {
  id?: number;
  reunion_id: number;
  nom: string;
  role?: string;
  email?: string;
  present?: boolean;
  notes_participant?: string;
}

export interface ComiteAgendaItem {
  id?: number;
  reunion_id: number;
  ordre: number;
  titre: string;
  description?: string;
  duree_estimee_min?: number;
  responsable?: string;
  statut?: string;
  notes_item?: string;
}

export interface ComiteNote {
  id?: number;
  reunion_id: number;
  type: 'note_generale' | 'decision' | 'enjeu' | 'risque';
  contenu: string;
  auteur?: string;
  priorite?: string;
  timestamp?: string;
}

export interface ComiteAction {
  id?: number;
  reunion_id: number;
  titre: string;
  description?: string;
  responsable: string;
  echeance?: string;
  priorite?: string;
  statut?: string;
  date_creation?: string;
  date_completion?: string;
  notes_progression?: string;
}

export interface ComiteResume {
  id?: number;
  reunion_id: number;
  resume_manuel?: string;
  resume_ai?: string;
  genere_par_ai?: boolean;
  date_generation?: string;
}

export interface ComiteReunionComplete extends ComiteReunion {
  participants?: ComiteParticipant[];
  agenda?: ComiteAgendaItem[];
  notes?: ComiteNote[];
  actions?: ComiteAction[];
  resume?: ComiteResume;
}

export interface ComiteDashboardTotals {
  total_reunions: number;
  en_cours: number;
  completees: number;
  valides: number;
  a_valider: number;
}

export interface ComiteDashboardClientSummary extends ComiteDashboardTotals {
  client_id: number;
  client_nom: string;
  /** Partie prenante BZ (client_id=2) responsable du comité pour ce client */
  responsable_pp_id?: number | null;
}

export interface ComiteDashboardResponse {
  totals: ComiteDashboardTotals;
  clients: ComiteDashboardClientSummary[];
}

export async function getComiteDashboard(scope: ComiteScope = 'CLIENT_TI'): Promise<ComiteDashboardResponse> {
  const response = await fetch(`${API_BASE_URL}/dashboard?scope=${encodeURIComponent(scope)}`);
  if (!response.ok) throw new Error('Erreur lors de la récupération du dashboard comité TI');
  return await response.json();
}

/** Définir la partie prenante BZ responsable du comité pour un client */
export async function setComiteClientResponsable(
  clientId: number,
  partiePrenanteId: number
): Promise<{ success: boolean; client_id: number; partie_prenante_id: number }> {
  const response = await fetch(`${API_BASE_URL}/client-responsable/${clientId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ partie_prenante_id: partiePrenanteId }),
  });
  if (!response.ok) throw new Error('Erreur lors de l\'enregistrement du responsable comité');
  return await response.json();
}

export async function getReunions(
  clientId: number = DEFAULT_CLIENT_ID,
  scope: ComiteScope = 'CLIENT_TI'
): Promise<ComiteReunion[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/reunions/${clientId}?scope=${encodeURIComponent(scope)}`);
    if (!response.ok) throw new Error('Erreur lors de la récupération des réunions');
    return await response.json();
  } catch (error) {
    console.error('Erreur getReunions:', error);
    return [];
  }
}

export async function getReunionComplete(reunionId: number): Promise<ComiteReunionComplete> {
  const response = await fetch(`${API_BASE_URL}/reunion/${reunionId}`);
  if (!response.ok) throw new Error('Erreur lors de la récupération de la réunion');
  return await response.json();
}

export async function createReunion(reunion: Partial<ComiteReunion>): Promise<any> {
  const clientId = reunion.clientId ?? reunion.client_id ?? DEFAULT_CLIENT_ID;
  const scope = (reunion.comiteScope || (reunion as any).scope || 'CLIENT_TI') as ComiteScope;
  const { client_id, ...rest } = reunion;
  const payload = {
    ...rest,
    clientId,
    comiteScope: scope,
  };

  const response = await fetch(`${API_BASE_URL}/reunion`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error('Erreur lors de la création de la réunion');
  const result = await response.json();

  // Créer automatiquement les items de checklist par défaut
  if (result.id || result.data?.id) {
    const reunionId = result.id || result.data.id;
    try {
      const { createDefaultChecklistItems } = await import('./checklistService');
      console.log(`Création des items de checklist par défaut pour la réunion ${reunionId}...`);
      const createdItems = await createDefaultChecklistItems(reunionId, 'Créateur de la réunion');
      console.log(`✅ ${createdItems.length} items de checklist créés avec succès`);
    } catch (checklistError) {
      // Ne pas bloquer la création de la réunion si la checklist échoue
      console.error(
        "❌ Erreur lors de la création des items de checklist par défaut:",
        checklistError
      );
    }
  }

  return result;
}

export async function updateReunion(reunionId: number, updates: Partial<ComiteReunion>): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/reunion/${reunionId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
  if (!response.ok) throw new Error('Erreur lors de la mise à jour de la réunion');
  return await response.json();
}

export async function deleteReunion(reunionId: number): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/reunion/${reunionId}`, {
    method: 'DELETE',
  });
  if (!response.ok) throw new Error('Erreur lors de la suppression de la réunion');
  return await response.json();
}

export async function addParticipant(participant: Partial<ComiteParticipant>): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/participant`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(participant),
  });
  if (!response.ok) throw new Error("Erreur lors de l'ajout du participant");
  return await response.json();
}

export async function updateParticipant(
  participantId: number,
  updates: Partial<ComiteParticipant>
): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/participant/${participantId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
  if (!response.ok) throw new Error('Erreur lors de la mise à jour du participant');
  return await response.json();
}

export async function deleteParticipant(participantId: number): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/participant/${participantId}`, {
    method: 'DELETE',
  });
  if (!response.ok) throw new Error('Erreur lors de la suppression du participant');
  return await response.json();
}

export async function addAgendaItem(item: Partial<ComiteAgendaItem>): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/agenda`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(item),
  });
  if (!response.ok) throw new Error("Erreur lors de l'ajout de l'item d'agenda");
  return await response.json();
}

export async function updateAgendaItem(agendaId: number, updates: Partial<ComiteAgendaItem>): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/agenda/${agendaId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
  if (!response.ok) throw new Error("Erreur lors de la mise à jour de l'item d'agenda");
  return await response.json();
}

export async function deleteAgendaItem(agendaId: number): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/agenda/${agendaId}`, {
    method: 'DELETE',
  });
  if (!response.ok) throw new Error("Erreur lors de la suppression de l'item d'agenda");
  return await response.json();
}

export async function addNote(note: Partial<ComiteNote>): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/note`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(note),
  });
  if (!response.ok) throw new Error("Erreur lors de l'ajout de la note");
  return await response.json();
}

export async function updateNote(noteId: number, updates: Partial<ComiteNote>): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/note/${noteId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
  if (!response.ok) throw new Error('Erreur lors de la mise à jour de la note');
  return await response.json();
}

export async function deleteNote(noteId: number): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/note/${noteId}`, {
    method: 'DELETE',
  });
  if (!response.ok) throw new Error('Erreur lors de la suppression de la note');
  return await response.json();
}

export async function addAction(action: Partial<ComiteAction>): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/action`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(action),
  });
  if (!response.ok) throw new Error("Erreur lors de l'ajout de l'action");
  return await response.json();
}

export async function updateAction(actionId: number, updates: Partial<ComiteAction>): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/action/${actionId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
  if (!response.ok) throw new Error("Erreur lors de la mise à jour de l'action");
  return await response.json();
}

export async function deleteAction(actionId: number): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/action/${actionId}`, {
    method: 'DELETE',
  });
  if (!response.ok) throw new Error("Erreur lors de la suppression de l'action");
  return await response.json();
}

export async function genererResume(
  reunionId: number,
  notes: string,
  decisions: string,
  enjeux: string,
  actions: string
): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/generer-resume`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reunion_id: reunionId, notes, decisions, enjeux, actions }),
  });
  if (!response.ok) throw new Error('Erreur lors de la génération du résumé');
  return await response.json();
}

export async function saveResume(
  reunionId: number,
  resume: string,
  genereParAI: boolean
): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/resume/${reunionId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      resume_manuel: genereParAI ? undefined : resume,
      resume_ai: genereParAI ? resume : undefined,
      genere_par_ai: genereParAI,
      date_generation: new Date().toISOString(),
    }),
  });
  if (!response.ok) throw new Error('Erreur lors de la sauvegarde du résumé');
  return await response.json();
}

